import React, { useContext, useEffect, useState } from "react";
import './Lifeline.css';
import './LifelineEvent.css';
import { getLifeline, updateLifelineEvent, deleteLifelineEvent, createLifelineEvent } from "../../../../api/api";
import { DashboardContext } from "../../DashboardContext";
import { AppContext } from "../../../../appContext";
import { LifelineEventEditModal } from "./LifelineEventEditModal";
import { LifelineEventAddModal } from "./LifelineEventAddModal";
import { GoodScroll } from "../../../desktopPage/GoodScroll";
import { useNavigate } from "react-router-dom";

interface ILifelineEventData {
    Datetime: string | null;
    Event: string;
    Description?: string;
    Level: 1 | 2;
    Good: boolean;
    id?: string;
    Image?: number;
    SessionId?: string;
    SessionDate?: number | string | any; // timestamp as number, string, or Firestore Timestamp object
}

interface ILifelineEventProps{
    data: ILifelineEventData;
    onEdit: (eventId: string, eventData: ILifelineEventData) => void;
}

export const LifelineEvent = ({data, onEdit}: ILifelineEventProps)=>{
    const { translate } = useContext(AppContext);
    const isImportant = data.Level === 2;
    const isBadEvent = !data.Good;

    // Отладочная информация
    console.log('Event:', data.Event, 'Level:', data.Level, 'Type of Level:', typeof data.Level, 'isImportant:', isImportant);

    // Компонент теперь рендерится всегда, так как фильтрация происходит на уровне списка
    // Отображаем дату как есть, включая "--.--.YYYY"

    // Импортируем изображение динамически с обработкой ошибок
    const getImageUrl = (imageId: number) => {
        try {
            // Проверяем, что imageId в допустимом диапазоне (0-151)
            const validImageId = (imageId >= 0 && imageId <= 151) ? imageId : 1;            
            // ВАЖНО: Проверяем validImageId = 0 ПОСЛЕ валидации
            if (validImageId === 0) {
                const imageName = data.Good ? 'good.png' : 'bad.png';

                let imageUrl;
                try {
                    if (data.Good) {
                        imageUrl = require(`../../../../assets/lifeline/good.png`);
                        console.log('>>> Loaded good.png:', imageUrl);
                    } else {
                        imageUrl = require(`../../../../assets/lifeline/bad.png`);
                        console.log('>>> Loaded bad.png:', imageUrl);
                    }
                } catch (specialImageError) {
                    console.warn('>>> Special images not found, using fallback numbered images');
                    console.warn('>>> Error:', specialImageError.message);

                    // Используем существующие изображения как fallback
                    // Например, 50.png для положительных (улыбка), 25.png для отрицательных (грусть)
                    const fallbackImageId = data.Good ? 50 : 25;
                    imageUrl = require(`../../../../assets/lifeline/${fallbackImageId}.png`);
                    console.log(`>>> Using fallback image ${fallbackImageId}.png:`, imageUrl);
                }

                console.log('>>> Final imageUrl:', imageUrl);
                console.log('=== getImageUrl DEBUG END (SUCCESS) ===');
                return imageUrl;
            }

            // Загружаем обычное пронумерованное изображение
            console.log(`Loading numbered image: ${validImageId}.png`);
            const numberedImage = require(`../../../../assets/lifeline/${validImageId}.png`);
            console.log('=== getImageUrl DEBUG END (NUMBERED) ===');
            return numberedImage;
        } catch (error) {
            console.error(`=== getImageUrl ERROR ===`);
            console.error(`Image not found for ID ${imageId}:`, error);
            console.error('Error details:', error.message);
            console.error('Error stack:', error.stack);
            // Возвращаем дефолтное изображение
            const fallbackImage = require(`../../../../assets/lifeline/1.png`);
            console.log('=== getImageUrl DEBUG END (FALLBACK) ===');
            return fallbackImage;
        }
    };

    const dashboardContext = useContext(DashboardContext);

    const navigate = useNavigate();

    const handleOpenSession = () => {
        if (data.SessionId && dashboardContext && dashboardContext.selectedClient) {
            // Находим сессию по ID
            const session = dashboardContext.selectedClient.sessions?.find(s => s.id === data.SessionId);

            if (session) {
                // Переключаемся на вкладку Sessions
                dashboardContext.setSelectedBottomMenuTabId('Sessions');

                // Открываем список сессий
                dashboardContext.setActivePopup('FullSessionList');

                // Автоматически переходим к анализу сессии (как делает кнопка Watch)
                setTimeout(() => {
                    navigate(`/meeting/${dashboardContext.selectedClient.id}/${data.SessionId}`);
                }, 100);
            }
        }
    };

    return <div className={`LifelineEvent_block ${!isImportant ? 'LifelineEvent_block--low-importance' : ''} ${isBadEvent ? 'LifelineEvent_block--negative' : ''}`}>
        <div className="LifelineEvent_image" style={{backgroundImage: `url("${getImageUrl(data.Image)}")`}}>

        </div>
        <div className="LifelineEvent_block_content">
            <div className="LifelineEvent_header">
                <div className="LifelineEvent_header_left">
                    <div 
                        className={`LifelineEvent_header_date ${data.SessionDate ? 'LifelineEvent_header_date--session' : ''}`}
                        onClick={data.SessionId ? handleOpenSession : undefined}
                        style={data.SessionId ? { cursor: 'pointer' } : {}}
                    >
                        {data.SessionDate ? 
                            (translate('lifeline_from_session_date') || 'Из сессии [date]').replace('[date]', formatSessionDate(data.SessionDate)) :
                            data.Datetime
                        }
                    </div>
                    <div className="LifelineEvent_header_title">
                        {data.Event}
                    </div>
                    {data.Description && (
                        <div className="LifelineEvent_description">
                            {data.Description}
                        </div>
                    )}
                </div>
                <div className="button LifelineEvent_header_edit" onClick={() => onEdit(data.id || '', data)}></div>
            </div>
        </div>
    </div>;
};

// Функция для парсинга даты с обработкой неполных дат
const parseDateForSorting = (dateString: string): Date | null => {
    try {
        if (!dateString) return null;

        // Обрабатываем даты вида "--.--.YYYY" или "--.MM.YYYY" или "DD.--.YYYY"
        let [day, month, year] = dateString.split('.');

        // Заменяем "--" на "01" для сортировки
        if (day === '--') day = '01';
        if (month === '--') month = '01';

        const parsedDay = parseInt(day);
        const parsedMonth = parseInt(month);
        const parsedYear = parseInt(year);

        if (isNaN(parsedDay) || isNaN(parsedMonth) || isNaN(parsedYear)) {
            return null;
        }

        return new Date(parsedYear, parsedMonth - 1, parsedDay);
    } catch (error) {
        return null;
    }
};

// Функция для вычисления возраста на дату события
const calculateAgeAtEvent = (birthDate: string, eventDate: string): number | null => {
    try {
        // Парсим дату рождения (формат ISO)
        const birth = new Date(birthDate);

        // Используем функцию парсинга для обработки неполных дат
        const event = parseDateForSorting(eventDate);

        if (!event || isNaN(birth.getTime()) || isNaN(event.getTime())) {
            return null;
        }

        let age = event.getFullYear() - birth.getFullYear();
        const monthDiff = event.getMonth() - birth.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && event.getDate() < birth.getDate())) {
            age--;
        }

        return age >= 0 ? age : null;
    } catch (error) {
        return null;
    }
};

// Функция для правильного склонения слова "год"
const getAgeText = (age: number, translate: (key: string) => string): string => {
    const lastDigit = age % 10;
    const lastTwoDigits = age % 100;

    // Исключения для 11-14
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${age} ${translate('age_years_5_plus')}`;
    }

    // Обычные правила
    if (lastDigit === 1) {
        return `${age} ${translate('age_years_1')}`;
    } else if (lastDigit >= 2 && lastDigit <= 4) {
        return `${age} ${translate('age_years_2_4')}`;
    } else {
        return `${age} ${translate('age_years_5_plus')}`;
    }
};

// Функция для форматирования даты из timestamp
const formatSessionDate = (timestamp: number | string | any): string => {
    // Проверяем, что timestamp существует
    if (!timestamp) {
        console.warn('Empty SessionDate timestamp:', timestamp);
        return '--.--.----';
    }

    let date: Date;

    try {
        // Случай 1: Firestore Timestamp объект (имеет метод toDate())
        if (timestamp && typeof timestamp === 'object' && typeof timestamp.toDate === 'function') {
            console.log('Processing Firestore Timestamp object:', timestamp);
            date = timestamp.toDate();
        }
        // Случай 2: Firestore Timestamp объект (имеет свойства seconds и nanoseconds)
        else if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
            console.log('Processing Firestore Timestamp with seconds:', timestamp.seconds);
            date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
        }
        // Случай 3: Строка в формате "July 7, 2025 at 12:00:00 AM UTC+3"
        else if (typeof timestamp === 'string') {
            console.log('Processing date string:', timestamp);
            // Удаляем "at" и "UTC+X" части для корректного парсинга
            const cleanedString = timestamp
                .replace(/\s+at\s+/g, ' ')
                .replace(/\s+UTC[+-]\d+$/g, '');
            date = new Date(cleanedString);
        }
        // Случай 4: Число (Unix timestamp в секундах)
        else if (typeof timestamp === 'number') {
            console.log('Processing numeric timestamp:', timestamp);
            // Если число больше 1e12, то это миллисекунды, иначе секунды
            const multiplier = timestamp > 1e12 ? 1 : 1000;
            date = new Date(timestamp * multiplier);
        }
        // Случай 5: Неизвестный формат
        else {
            console.warn('Unknown SessionDate format:', typeof timestamp, timestamp);
            return '--.--.----';
        }

        // Проверяем, что дата корректная
        if (isNaN(date.getTime())) {
            console.warn('Invalid date created from timestamp:', timestamp);
            return '--.--.----';
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        console.log('Successfully formatted date:', `${day}.${month}.${year}`, 'from:', timestamp);
        return `${day}.${month}.${year}`;

    } catch (error) {
        console.error('Error formatting SessionDate:', error, 'Original timestamp:', timestamp);
        return '--.--.----';
    }
};

interface ILifelineProps{
    onClose: ()=>void;
}

export const Lifeline = ({onClose}: ILifelineProps)=>{
    const [events, setEvents] = useState<ILifelineEventData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingEvent, setEditingEvent] = useState<{id: string, data: ILifelineEventData} | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEventsWithoutDates, setShowEventsWithoutDates] = useState(false);
    const { selectedClient } = useContext(DashboardContext);
    const { translate } = useContext(AppContext);

    useEffect(() => {
        let isCancelled = false;

        const loadLifelineData = async () => {
            if (!selectedClient) return;

            try {
                setLoading(true);
                setError(null);
                console.log('Loading lifeline for client:', selectedClient.id);
                const response = await getLifeline(selectedClient.id);

                if (isCancelled) return; // Компонент размонтирован

                if (response.ok) {
                    const data = await response.json();
                    console.log('Lifeline API response:', data);

                    // Преобразуем данные из формата [{id: {event}}] в формат [event]
                    const transformedEvents: ILifelineEventData[] = [];
                    if (Array.isArray(data)) {
                        data.forEach(item => {
                            // Каждый элемент - это объект с одним ключом (ID) и значением (событие)
                            const eventId = Object.keys(item)[0];
                            const eventData = item[eventId];
                            if (eventData && typeof eventData === 'object') {
                                transformedEvents.push({
                                    id: eventId,
                                    Datetime: eventData.Datetime || null,
                                    Event: eventData.Event || '',
                                    Description: eventData.Description || '',
                                    Level: eventData.Level || 1,
                                    Good: eventData.Good !== undefined ? eventData.Good : true,
                                    Image: eventData.Image !== undefined ? eventData.Image : 1,
                                    SessionId: eventData.SessionId,
                                    SessionDate: eventData.SessionDate
                                });
                            }
                        });
                    }

                    if (!isCancelled) {
                        console.log('Transformed events:', transformedEvents);
                        console.log('Events with valid dates:', transformedEvents.filter(event => event.Datetime !== null));
                        setEvents(transformedEvents);
                        console.log('Events state updated successfully');
                    }
                } else {
                    if (!isCancelled) {
                        console.error('Failed to load lifeline data, response status:', response.status);
                        setError('Failed to load lifeline data');
                    }
                }
            } catch (err) {
                if (!isCancelled) {
                    setError('Error loading lifeline data');
                    console.error('Lifeline loading error:', err);
                }
            } finally {
                if (!isCancelled) {
                    console.log('Setting loading to false');
                    setLoading(false);
                }
            }
        };

        console.log('Starting lifeline data load for:', selectedClient?.id);
        loadLifelineData();

        return () => {
            isCancelled = true;
        };
    }, [selectedClient?.id]);

    const handleEditEvent = (eventId: string, eventData: ILifelineEventData) => {
        // Передаем данные в формате, ожидаемом модальным окном редактирования
        const mappedData = {
            Datetime: eventData.Datetime,
            Event: eventData.Event,
            Description: eventData.Description,
            Level: eventData.Level,
            Good: eventData.Good
        };
        setEditingEvent({ id: eventId, data: mappedData });
    };

    const handleSaveEvent = async (eventId: string, updatedData: any) => {
        console.log('=== HANDLE SAVE EVENT DEBUG START ===');
        console.log('Selected client:', selectedClient?.id);
        console.log('Event ID to update:', eventId);
        console.log('Updated data received:', updatedData);

        if (!selectedClient) {
            console.error('No selected client');
            return;
        }

        try {
            console.log('=== CALLING updateLifelineEvent API ===');
            const response = await updateLifelineEvent(selectedClient.id, eventId, updatedData);
            console.log('API response status:', response.status);
            console.log('API response ok:', response.ok);

            if (response.ok) {
                console.log('=== API call successful, updating local state ===');
                // Обновляем локальные данные с правильным маппингом полей
                setEvents(prevEvents => 
                    prevEvents.map(event => 
                        event.id === eventId 
                            ? {
                                ...event,
                                Datetime: updatedData.Datetime || event.Datetime,
                                Event: updatedData.Event || event.Event,
                                Description: updatedData.Description !== undefined ? updatedData.Description : event.Description,
                                Level: updatedData.Level !== undefined ? updatedData.Level : event.Level,
                                Good: updatedData.Good !== undefined ? updatedData.Good : event.Good,
                                Image: event.Image
                              }
                            : event
                    )
                );
                console.log('=== Local state updated successfully ===');
            } else {
                const errorText = await response.text();
                console.error('API response not ok. Status:', response.status, 'Text:', errorText);
                throw new Error(`Failed to update event. Status: ${response.status}, Response: ${errorText}`);
            }
        } catch (error) {
            console.error('=== ERROR in handleSaveEvent ===');
            console.error('Error type:', typeof error);
            console.error('Error message:', error?.message || 'Unknown error');
            console.error('Full error object:', error);
            console.error('Stack trace:', error?.stack);
            throw error;
        }
    };

    const handleDeleteEvent = async (eventId: string) => {
        if (!selectedClient) return;

        try {
            const response = await deleteLifelineEvent(selectedClient.id, eventId);
            if (response.ok) {
                // Удаляем событие из локальных данных
                setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
            } else {
                throw new Error('Failed to delete event');
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            throw error;
        }
    };

    const handleCloseEditModal = () => {
        setEditingEvent(null);
    };

    const handleAddEvent = () => {
        setShowAddModal(true);
    };

    const handleCreateEvent = async (eventData: any) => {
        if (!selectedClient) return;

        try {
            const response = await createLifelineEvent(selectedClient.id, eventData);
            if (response.ok) {
                // Перезагружаем данные после успешного создания
                const lifelineResponse = await getLifeline(selectedClient.id);
                if (lifelineResponse.ok) {
                    const data = await lifelineResponse.json();
                    const transformedEvents: ILifelineEventData[] = [];
                    if (Array.isArray(data)) {
                        data.forEach(item => {
                            const eventId = Object.keys(item)[0];
                            const eventData = item[eventId];
                            if (eventData && typeof eventData === 'object') {
                                transformedEvents.push({
                                    id: eventId,
                                    Datetime: eventData.Datetime || null,
                                    Event: eventData.Event || '',
                                    Description: eventData.Description || '',
                                    Level: eventData.Level || 1,
                                    Good: eventData.Good !== undefined ? eventData.Good : true,
                                    Image: eventData.Image || 1,
                                    SessionId: eventData.SessionId,
                                    SessionDate: eventData.SessionDate
                                });
                            }
                        });
                    }
                    setEvents(transformedEvents);
                }
            } else {
                throw new Error('Failed to create event');
            }
        } catch (error) {
            console.error('Error creating event:', error);
            alert(translate('lifeline_create_error'));
            throw error;
        }
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    return (
        <div className="Lifeline">
            <div className="Lifeline_form">
                <div className="Lifeline_header">
                    <div className="Lifeline_title">
                        {translate('lifeline_title')}
                    </div>
                    <div className="Lifeline_header_actions">
                        <button 
                            className="Lifeline_toggle_events_button" 
                            onClick={() => setShowEventsWithoutDates(!showEventsWithoutDates)}
                        >
                            {showEventsWithoutDates ? translate('events') : translate('events_without_dates')}
                        </button>
                        <button className="Lifeline_add_event_button" onClick={handleAddEvent}>
                            {translate('lifeline_add_event')}
                        </button>
                    </div>
                </div>
                <div className="Lifeline_main">
                    {loading && <div className="Lifeline_loading">{translate('lifeline_loading')}</div>}
                    {!loading && (
                        <div className="Lifeline_events_container">
                            <GoodScroll>
                                <div className="Lifeline_events_wrapper">
                                    <div className="Lifeline_timeline_background"></div>
                                    <div className="Lifeline_events">
                                        {(() => {
                                            let validEvents;

                                        if (showEventsWithoutDates) {
                                            // Показываем события без года (где не удается спарсить год)
                                            validEvents = events.filter(event => {
                                                if (event.Datetime === null || 
                                                    event.Datetime === undefined || 
                                                    event.Datetime.trim() === '') {
                                                    return true; // Показываем события без даты вообще
                                                }

                                                // Проверяем, можно ли спарсить год (4 цифры)
                                                const dateParts = event.Datetime.split('.');
                                                if (dateParts.length >= 3) {
                                                    const year = dateParts[2];
                                                    // Проверяем, что год состоит из 4 цифр
                                                    const yearMatch = year.match(/^\d{4}$/);
                                                    return !yearMatch; // Возвращаем события, где год НЕ парсится как 4 цифры
                                                }

                                                return true; // Если формат даты неправильный, показываем в списке без дат
                                            });
                                        } else {
                                            // Обычная фильтрация: только с датой где можно спарсить год (4 цифры)
                                            validEvents = events.filter(event => {
                                                if (event.Datetime === null || 
                                                    event.Datetime === undefined || 
                                                    event.Datetime.trim() === '') {
                                                    return false;
                                                }

                                                // Проверяем, можно ли спарсить год (4 цифры)
                                                const dateParts = event.Datetime.split('.');
                                                if (dateParts.length >= 3) {
                                                    const year = dateParts[2];
                                                    // Проверяем, что год состоит из 4 цифр
                                                    const yearMatch = year.match(/^\d{4}$/);
                                                    return !!yearMatch; // Возвращаем события, где год парсится как 4 цифры
                                                }

                                                return false; // Если формат даты неправильный, не показываем в основном списке
                                            });
                                        }

                                        if (validEvents.length === 0) {
                                            return <div className="Lifeline_empty">{translate('lifeline_no_events')}</div>;
                                        }

                                        // Сортируем события по дате (самые свежие события сверху)
                                        const sortedEvents = validEvents.sort((a, b) => {
                                            const dateA = parseDateForSorting(a.Datetime);
                                            const dateB = parseDateForSorting(b.Datetime);

                                            if (!dateA && !dateB) return 0;
                                            if (!dateA) return 1;
                                            if (!dateB) return -1;

                                            return dateB.getTime() - dateA.getTime();
                                        });

                                        return sortedEvents.map((event, index) => {
                                            const age = selectedClient?.dateOfBirth ? 
                                                calculateAgeAtEvent(selectedClient.dateOfBirth, event.Datetime) : null;

                                            // Определяем, нужно ли показывать возраст (год)
                                            // Показываем только если это первое событие или год отличается от предыдущего
                                            let showAge = false;
                                            if (age !== null) {
                                                if (index === 0) {
                                                    // Первое событие - всегда показываем
                                                    showAge = true;
                                                } else {
                                                    // Сравниваем год с предыдущим событием
                                                    const prevEvent = sortedEvents[index - 1];
                                                    const prevAge = selectedClient?.dateOfBirth ? 
                                                        calculateAgeAtEvent(selectedClient.dateOfBirth, prevEvent.Datetime) : null;

                                                    showAge = prevAge === null || age !== prevAge;
                                                }
                                            }

                                            return (
                                                <div key={event.id || index} className="Lifeline_item">
                                                    <div className={`Lifeline_item_sizer ${!event.Good ? 'Lifeline_item_sizer--negative' : ''}`}>
                                                        <LifelineEvent data={event} onEdit={handleEditEvent}></LifelineEvent>
                                                    </div>
                                                    <div className="Lifeline_dots">
                                                        {showAge && (
                                                            <div className="Lifeline_age">
                                                                {getAgeText(age, translate)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </GoodScroll>
                    </div>
                    )}
                </div>
            </div>

            {editingEvent && (
                <LifelineEventEditModal
                    eventId={editingEvent.id}
                    eventData={editingEvent.data}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveEvent}
                    onDelete={handleDeleteEvent}
                />
            )}

            {showAddModal && (
                <LifelineEventAddModal
                    onClose={handleCloseAddModal}
                    onSave={handleCreateEvent}
                />
            )}
        </div>
    )
}